import React, { useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  BarChart3,
  Bell,
  Building2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CreditCard,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  MoreVertical,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Store,
  Wallet,
  X,
} from 'lucide-react';

import './ControlCenterVendors.css';

const ControlCenterVendors = () => {
  const navigate = useNavigate();

  const outletContext = useOutletContext();

  let savedAdmin = null;

  try {
    const rawAdmin =
      localStorage.getItem('vakra_control_admin') ||
      sessionStorage.getItem('vakra_control_admin');

    if (rawAdmin) {
      savedAdmin = JSON.parse(rawAdmin);
    }
  } catch (error) {
    console.error('Saved admin parse error:', error);
  }

  const admin =
    outletContext?.admin ||
    savedAdmin ||
    null;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [businessType, setBusinessType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const loadVendors = async () => {
      try {
        setLoading(true);

        const token =
          localStorage.getItem('vakra_control_admin_token') ||
          sessionStorage.getItem('vakra_control_admin_token');

        const response = await fetch(
          `${window.location.origin}/api/control-center/vendors`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 404) {
          setVendors([]);
          return;
        }

        const data = await response.json().catch(() => ({}));

        if (response.ok && Array.isArray(data.vendors)) {
          setVendors(data.vendors);
        } else {
          setVendors([]);
        }
      } catch (error) {
        console.error('Vendor load error:', error);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, []);

  const filteredVendors = useMemo(() => {
    const query = search.trim().toLowerCase();

    return vendors.filter((vendor) => {
      const matchesSearch =
        !query ||
        String(vendor.businessName || '')
          .toLowerCase()
          .includes(query) ||
        String(vendor.ownerName || '')
          .toLowerCase()
          .includes(query) ||
        String(vendor.mobile || '')
          .toLowerCase()
          .includes(query) ||
        String(vendor.vendorId || '')
          .toLowerCase()
          .includes(query);

      const matchesBusiness =
        businessType === 'all' ||
        vendor.businessType === businessType;

      const matchesStatus =
        statusFilter === 'all' ||
        vendor.status === statusFilter;

      return matchesSearch && matchesBusiness && matchesStatus;
    });
  }, [vendors, search, businessType, statusFilter]);

  const totals = useMemo(() => {
    return {
      total: vendors.length,

      active: vendors.filter(
        (vendor) => vendor.status === 'active'
      ).length,

      expiring: vendors.filter(
        (vendor) => vendor.status === 'expiring'
      ).length,

      expired: vendors.filter(
        (vendor) => vendor.status === 'expired'
      ).length,
    };
  }, [vendors]);

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/control-center/dashboard',
    },
    {
      label: 'Vendors',
      icon: Building2,
      path: '/control-center/vendors',
      active: true,
    },
    {
      label: 'Subscriptions',
      icon: CreditCard,
      path: '/control-center/subscriptions',
    },
    {
      label: 'Payments',
      icon: Wallet,
      path: '/control-center/payments',
    },
    {
      label: 'Agreements',
      icon: FileText,
      path: '/control-center/agreements',
    },
    {
      label: 'Smart Offer Digest',
      icon: Sparkles,
      path: '/control-center/smart-digest',
    },
    {
      label: 'Notifications',
      icon: Bell,
      path: '/control-center/notifications',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path: '/control-center/analytics',
    },
    {
      label: 'Support',
      icon: MessageSquare,
      path: '/control-center/support',
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/control-center/settings',
    },
  ];

  const handleMenuClick = (item) => {
    setSidebarOpen(false);

    if (
      item.path === '/control-center/vendors' ||
      item.path === '/control-center/dashboard' ||
      item.path === '/control-center/subscriptions'||
      item.path === '/control-center/payments'||
      item.path === '/control-center/agreements'||
      item.path === '/control-center/smart-digest'||
      item.path === '/control-center/notifications'
    ) {
      navigate(item.path);
      return;
    }

    alert(`${item.label} page next phase me banega.`);
  };

  const handleLogout = async () => {
    const token =
      localStorage.getItem('vakra_control_admin_token') ||
      sessionStorage.getItem('vakra_control_admin_token');

    try {
      if (token) {
        await fetch(
          `${window.location.origin}/api/control-center/logout`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('vakra_control_admin_token');
    localStorage.removeItem('vakra_control_admin');

    sessionStorage.removeItem('vakra_control_admin_token');
    sessionStorage.removeItem('vakra_control_admin');

    navigate('/control-center/login', {
      replace: true,
    });
  };

  const getStatusClass = (status) => {
    if (status === 'active') {
      return 'active';
    }

    if (status === 'expiring') {
      return 'expiring';
    }

    if (status === 'expired') {
      return 'expired';
    }

    return 'inactive';
  };

  return (
    <div className="vccv-app">
      {sidebarOpen && (
        <button
          type="button"
          className="vccv-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        />
      )}

      <aside
        className={`vccv-sidebar ${
          sidebarOpen ? 'vccv-sidebar-open' : ''
        }`}
      >
        <div className="vccv-brand-row">
          <div className="vccv-brand">
            <div className="vccv-logo">
              V
            </div>

            <div>
              <strong>
                VAKRA
              </strong>

              <span>
                CONTROL CENTER
              </span>
            </div>
          </div>

          <button
            type="button"
            className="vccv-close"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <div className="vccv-admin-card">
          <div className="vccv-admin-avatar">
            {String(admin?.name || 'A')
              .charAt(0)
              .toUpperCase()}
          </div>

          <div>
            <strong>
              {admin?.name || 'Administrator'}
            </strong>

            <span>
              {admin?.role || 'admin'}
            </span>
          </div>
        </div>

        <nav className="vccv-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.label}
                type="button"
                className={`vccv-nav-item ${
                  item.active ? 'active' : ''
                }`}
                onClick={() => handleMenuClick(item)}
              >
                <Icon size={18} />

                <span>
                  {item.label}
                </span>

                {item.active && <i />}
              </button>
            );
          })}
        </nav>

        <div className="vccv-sidebar-bottom">
          <div className="vccv-secure">
            <ShieldCheck size={19} />

            <div>
              <strong>
                Secure Admin
              </strong>

              <span>
                Protected session
              </span>
            </div>
          </div>

          <button
            type="button"
            className="vccv-logout"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      <main className="vccv-main">
        <header className="vccv-topbar">
          <div className="vccv-top-left">
            <button
              type="button"
              className="vccv-mobile-menu"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={21} />
            </button>

            <div>
              <p>
                VAKRA CONTROL CENTER
              </p>

              <h1>
                Vendors
              </h1>
            </div>
          </div>

          <div className="vccv-top-right">
            <button
              type="button"
              className="vccv-notification"
            >
              <Bell size={19} />
            </button>

            <div className="vccv-top-profile">
              <div className="vccv-profile-avatar">
                {String(admin?.name || 'A')
                  .charAt(0)
                  .toUpperCase()}
              </div>

              <div>
                <strong>
                  {admin?.name || 'Admin'}
                </strong>

                <span>
                  {admin?.email || ''}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="vccv-content">
          <section className="vccv-page-heading">
            <div>
              <span>
                <Store size={16} />
                Vendor Management
              </span>

              <h2>
                Manage all VAKRA vendors
              </h2>

              <p>
                Vendor profile, business type, subscription,
                payment status aur expiry ek jagah se manage
                karein.
              </p>
            </div>

            <button
              type="button"
              className="vccv-add-button"
              onClick={() =>
                alert(
                  'Add Vendor form next step me connect karenge.'
                )
              }
            >
              <Plus size={19} />
              Add Vendor
            </button>
          </section>

          <section className="vccv-stats">
            <article>
              <div className="vccv-stat-icon">
                <Building2 size={21} />
              </div>

              <div>
                <span>Total Vendors</span>
                <strong>{totals.total}</strong>
                <small>All business categories</small>
              </div>
            </article>

            <article>
              <div className="vccv-stat-icon green">
                <ShieldCheck size={21} />
              </div>

              <div>
                <span>Active</span>
                <strong>{totals.active}</strong>
                <small>Active subscription</small>
              </div>
            </article>

            <article>
              <div className="vccv-stat-icon yellow">
                <CircleAlert size={21} />
              </div>

              <div>
                <span>Expiring Soon</span>
                <strong>{totals.expiring}</strong>
                <small>Renewal attention</small>
              </div>
            </article>

            <article>
              <div className="vccv-stat-icon red">
                <CreditCard size={21} />
              </div>

              <div>
                <span>Expired</span>
                <strong>{totals.expired}</strong>
                <small>Subscription expired</small>
              </div>
            </article>
          </section>

          <section className="vccv-table-panel">
            <div className="vccv-toolbar">
              <div className="vccv-search">
                <Search size={18} />

                <input
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search business, owner, mobile or Vendor ID"
                />
              </div>

              <div className="vccv-filters">
                <label>
                  <Building2 size={16} />

                  <select
                    value={businessType}
                    onChange={(event) =>
                      setBusinessType(event.target.value)
                    }
                  >
                    <option value="all">
                      All Businesses
                    </option>

                    <option value="fastfood">
                      Fast Food
                    </option>

                    <option value="grocery">
                      Grocery
                    </option>

                    <option value="garments">
                      Garments
                    </option>

                    <option value="cosmetic">
                      Cosmetics
                    </option>
                  </select>

                  <ChevronDown size={15} />
                </label>

                <label>
                  <select
                    value={statusFilter}
                    onChange={(event) =>
                      setStatusFilter(event.target.value)
                    }
                  >
                    <option value="all">
                      All Status
                    </option>

                    <option value="active">
                      Active
                    </option>

                    <option value="expiring">
                      Expiring
                    </option>

                    <option value="expired">
                      Expired
                    </option>

                    <option value="inactive">
                      Inactive
                    </option>
                  </select>

                  <ChevronDown size={15} />
                </label>
              </div>
            </div>

            <div className="vccv-table-heading">
              <div>
                <h3>
                  Vendor Directory
                </h3>

                <p>
                  {filteredVendors.length} vendor
                  {filteredVendors.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="vccv-loading">
                <div className="vccv-spinner" />

                <strong>
                  Loading vendors...
                </strong>

                <span>
                  Vendor data fetch ho raha hai.
                </span>
              </div>
            ) : filteredVendors.length === 0 ? (
              <div className="vccv-empty">
                <div>
                  <Store size={28} />
                </div>

                <h3>
                  No vendors found
                </h3>

                <p>
                  Vendor API connect hone ke baad existing
                  VAKRA vendors automatically yahan show honge.
                </p>
              </div>
            ) : (
              <div className="vccv-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Business</th>
                      <th>Contact</th>
                      <th>Plan</th>
                      <th>Expiry</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {filteredVendors.map((vendor) => (
                      <tr key={vendor.vendorId}>
                        <td>
                          <div className="vccv-vendor-cell">
                            <div className="vccv-shop-avatar">
                              {String(
                                vendor.businessName || 'V'
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <strong>
                                {vendor.businessName}
                              </strong>

                              <span>
                                ID: {vendor.vendorId}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="vccv-business-badge">
                            {vendor.businessType || '—'}
                          </span>
                        </td>

                        <td>
                          <div className="vccv-contact-cell">
                            <strong>
                              {vendor.ownerName || '—'}
                            </strong>

                            <span>
                              {vendor.mobile || '—'}
                            </span>
                          </div>
                        </td>

                        <td>
                          <span>
                            {vendor.planName || 'No Plan'}
                          </span>
                        </td>

                        <td>
                          {vendor.expiryDate || '—'}
                        </td>

                        <td>
                          <span
                            className={`vccv-status ${getStatusClass(
                              vendor.status
                            )}`}
                          >
                            <i />

                            {vendor.status || 'inactive'}
                          </span>
                        </td>

                        <td>
                          <div className="vccv-row-actions">
                            <button
                              type="button"
                              className="vccv-view-button"
                              onClick={() =>
                                navigate(
                                  `/control-center/vendors/${vendor.vendorId}`
                                )
                              }
                            >
                              View

                              <ChevronRight size={15} />
                            </button>

                            <button
                              type="button"
                              className="vccv-more"
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default ControlCenterVendors;
